import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import './ListingDetail.css';

const CONDITION_LABELS = { new:'New', like_new:'Like New', good:'Good', fair:'Fair', parts_only:'Parts Only' };
const CATEGORY_LABELS  = { street_legal:'Street Legal', mini_moto:'Mini Moto', medium_moto:'Medium Moto', mega_moto:'Mega Moto', part:'Part', accessory:'Accessory' };

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [listing, setListing]       = useState(null);
  const [seller,  setSeller]        = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeImg, setActiveImg]   = useState(0);
  const [message, setMessage]       = useState('');
  const [sending, setSending]       = useState(false);
  const [sent,    setSent]          = useState(false);
  const [msgErr,  setMsgErr]        = useState('');

  useEffect(() => { fetchListing(); }, [id]);

  async function fetchListing() {
    const { data } = await supabase.from('listings').select('*, profiles(*)').eq('id', id).single();
    if (data) {
      setListing(data); setSeller(data.profiles);
      await supabase.from('listings').update({ views: (data.views||0)+1 }).eq('id', id);
    }
    setLoading(false);
  }

  async function sendMessage(e) {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!message.trim()) return;
    setSending(true); setMsgErr('');
    const { error } = await supabase.from('messages').insert({
      listing_id: listing.id, sender_id: user.id,
      receiver_id: listing.seller_id, content: message.trim(),
    });
    if (error) setMsgErr('Failed to send. Please try again.');
    else { setSent(true); setMessage(''); }
    setSending(false);
  }

  async function deleteListing() {
    if (!window.confirm('Delete this listing?')) return;
    await supabase.from('listings').update({ is_active: false }).eq('id', id);
    navigate('/my-listings');
  }

  if (loading) return <div className="spinner" />;
  if (!listing) return (
    <div className="container empty-state" style={{ paddingTop: 80 }}>
      <h3>Listing not found</h3>
      <Link to="/listings" className="btn btn-outline" style={{ marginTop: 20 }}>Back to Browse</Link>
    </div>
  );

  const isOwner = user?.id === listing.seller_id;
  const images  = listing.images?.length > 0 ? listing.images : [];

  return (
    <div className="listing-detail page-enter">
      <div className="container">
        <Link to="/listings" className="back-link">← All Listings</Link>

        <div className="detail-layout">
          {/* Images */}
          <div>
            <div className="main-image">
              {images.length > 0
                ? <img src={images[activeImg]} alt={listing.title} />
                : <div className="no-image"><span>{CATEGORY_LABELS[listing.category]?.[0] || '?'}</span><p>No photos added</p></div>
              }
            </div>
            {images.length > 1 && (
              <div className="image-thumbs">
                {images.map((img, i) => (
                  <button key={i} className={`thumb ${i === activeImg ? 'active' : ''}`} onClick={() => setActiveImg(i)}>
                    <img src={img} alt={`View ${i+1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="detail-badges">
              <span className="badge badge-violet">{CATEGORY_LABELS[listing.category] || listing.category}</span>
              <span className={`badge ${listing.condition === 'new' ? 'badge-cyan' : listing.condition === 'parts_only' ? 'badge-red' : 'badge-violet'}`}>
                {CONDITION_LABELS[listing.condition]}
              </span>
            </div>

            <h1 className="detail-title">{listing.title}</h1>
            <div className="detail-price mono">${Number(listing.price).toLocaleString()}</div>

            {(listing.brand || listing.model || listing.year || listing.mileage) && (
              <div className="detail-specs">
                {listing.year    && <div className="spec-item"><span>Year</span><strong>{listing.year}</strong></div>}
                {listing.brand   && <div className="spec-item"><span>Brand</span><strong>{listing.brand}</strong></div>}
                {listing.model   && <div className="spec-item"><span>Model</span><strong>{listing.model}</strong></div>}
                {listing.mileage != null && <div className="spec-item"><span>Mileage</span><strong>{listing.mileage.toLocaleString()} mi</strong></div>}
              </div>
            )}

            {listing.location && <p className="detail-location">Location — {listing.location}</p>}

            {listing.description && (
              <div className="detail-description">
                <h3>Description</h3>
                <p>{listing.description}</p>
              </div>
            )}

            {seller && (
              <div className="seller-card">
                <div className="seller-avatar">
                  {seller.avatar_url ? <img src={seller.avatar_url} alt={seller.username} /> : <span>{(seller.username||'U')[0].toUpperCase()}</span>}
                </div>
                <div>
                  <p className="seller-name">{seller.username}</p>
                  <p className="seller-meta">Listed {new Date(listing.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            )}

            {isOwner ? (
              <div className="owner-actions">
                <Link to={`/sell/edit/${listing.id}`} className="btn btn-outline">Edit</Link>
                <button className="btn btn-danger" onClick={deleteListing}>Delete</button>
              </div>
            ) : (
              <div className="contact-section">
                {sent ? (
                  <div className="alert alert-success">Message sent. The seller will get back to you.</div>
                ) : (
                  <form onSubmit={sendMessage} className="message-form">
                    <h3>Message Seller</h3>
                    {msgErr && <div className="alert alert-error">{msgErr}</div>}
                    <textarea
                      className="form-textarea"
                      placeholder={user ? "Hi, I'm interested in this listing..." : "Log in to message the seller"}
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      disabled={!user}
                      required
                    />
                    {user
                      ? <button type="submit" className="btn btn-primary" style={{ width:'100%' }} disabled={sending}>{sending ? 'Sending...' : 'Send Message'}</button>
                      : <Link to="/login" className="btn btn-primary" style={{ display:'block', textAlign:'center' }}>Log In to Message</Link>
                    }
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
